import type { FC } from "react"
import { useContext } from "react"
import { Box } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { PickersDay } from "@mui/x-date-pickers"
import type { PickersDayProps } from "@mui/x-date-pickers"
import type { DateTime } from "luxon"

import type { Band } from "./range"
import { DAY_RING_WIDTH, RangeContext, getRangeMark } from "./range"

/** MUI's space between days, moved onto the band so it runs unbroken. */
const DAY_GAP = "2px"
const BAND_OPACITY = 0.14

/**
 * A calendar day that shows the whole range: both ends filled, the days between them on a
 * band. Only the end being edited is the calendar's selected day; the other just looks it.
 */
const RangeDay: FC<PickersDayProps<DateTime>> = ({
  day,
  outsideCurrentMonth,
  ...props
}) => {
  const range = useContext(RangeContext)
  // Days of the next or previous month are blank fillers here.
  const { isEnd, band } = outsideCurrentMonth
    ? { isEnd: false, band: null }
    : getRangeMark(day, range)

  return (
    <Box
      sx={(theme) => {
        const color = alpha(theme.palette.primary.main, BAND_OPACITY)
        const backgrounds: Record<Exclude<Band, null>, string> = {
          full: color,
          right: `linear-gradient(to right, transparent 50%, ${color} 50%)`,
          left: `linear-gradient(to left, transparent 50%, ${color} 50%)`,
        }
        return { px: DAY_GAP, background: band ? backgrounds[band] : "transparent" }
      }}
    >
      <PickersDay
        {...props}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        disableMargin
        sx={(theme) => ({
          ...(isEnd && {
            "&, &:hover, &:focus": {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            },
          }),
          // The end the calendar edits gets a ring around its fill.
          "&.Mui-selected": {
            boxShadow: `0 0 0 ${DAY_RING_WIDTH}px ${alpha(theme.palette.primary.main, 0.35)}`,
          },
        })}
      />
    </Box>
  )
}

export default RangeDay
